import DOMPurify from "dompurify";
import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { getLayerTitle } from "../../esri/custom-popup-content";
import { createSymbol, getLayerIconSymbology } from "../../utils/symbologies";
import { LayerIconWrapper } from "../ManualWidgets/ManualWidget-styled";
import { iconNameToIcon, svgToBase } from "./layerSvg/clusterAssets";

const StyledTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  text-transform: uppercase;
`;

function removeStyleFromSvg(svgString) {
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
  const styleElement = svgDoc.querySelector("style");
  if (styleElement) {
    styleElement.remove();
  }
  return svgDoc.documentElement.outerHTML;
}

const LayerIcon = ({
  config,
  layer,
  t,
  fill,
  opsColor,
  width = 40,
  height = 40,
  selected,
  textColor = "#fff",
  textStyles = {},
  wrapperStyles = {},
  showTitle = true,
  defaultTitle = "",
  circleColor,
}) => {
  const getLayerDefaultSymbol = useCallback(
    (layer) => {
      if (!layer) {
        return null;
      }

      const symbology = getLayerIconSymbology(layer, config);
      if (!symbology) {
        if (layer.renderer && layer.renderer.defaultSymbol) {
          return {
            type: "picture-marker",
            url: layer.renderer.defaultSymbol.url,
          };
        }
        return null;
      }

      const symbol = createSymbol(
        symbology,
        layer,
        symbology.defaultIcon,
        symbology.defaultColor,
        symbology.defaultSize
      );
      const iconName = symbology?.layerIcon || symbology?.defaultIcon || "";

      let icon;

      if (iconName) {
        const svgString = iconNameToIcon(iconName);

        if (svgString) {
          icon = removeStyleFromSvg(svgString);
        }
      }

      switch (symbol?.type) {
        case "picture-marker":
          return { ...symbol, icon };
        case "simple-fill":
          return symbol;
        default:
          return {
            type: icon ? "picture-marker" : symbol?.type,
            url: svgToBase(symbology.defaultIcon, symbology.defaultColor),
            icon,
          };
      }
    },
    [config, selected]
  );

  const symbol = useMemo(() => {
    return getLayerDefaultSymbol(layer);
  }, [layer, getLayerDefaultSymbol]);

  const layerTitle = useMemo(
    () => getLayerTitle(layer, t) || defaultTitle,
    [layer, t, defaultTitle]
  );

  const renderDefaultOrSimpleFill = () => (
    <StyledTextContainer
      style={{
        width: width - 10,
        height: height - 10,
        color: textColor,
        ...textStyles,
      }}
    >
      <span style={{ color: "inherit" }}>
        {layerTitle?.length > 0 ? layerTitle[0] : ""}
      </span>
    </StyledTextContainer>
  );

  const safeIcon = symbol?.icon ? DOMPurify.sanitize(symbol.icon) : "";

  const renderIcon = () => (
    <LayerIconWrapper
      width={width}
      height={height}
      opsColor={opsColor}
      fill={fill}
      selected={selected}
      circleColor={circleColor}
      style={wrapperStyles}
      isReport={layer?.layerConfig.isShownInReportManager}
      dangerouslySetInnerHTML={{ __html: safeIcon }}
    />
  );

  if (
    !symbol ||
    (symbol && (symbol.type === "simple-fill" || !symbol.icon) && showTitle)
  ) {
    return renderDefaultOrSimpleFill();
  }

  if (symbol && symbol.icon) {
    return renderIcon();
  } else {
    return null;
  }
};

export default LayerIcon;
